// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@awc/App/Store/Reducers";
import { xIsPLPTableView } from "@awc/App/Store/Selectors/GeneralSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { productListCategoryFiltersStyles } from "@insite/content-library/Widgets/ProductList/ProductListCategoryFilters";
import ProductListFiltersAccordionSection, {
    ProductListFilterAccordionSectionStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    expandByDefault = "expandByDefault",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.expandByDefault]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
    // XNG-Changes
    showTableLayout: xIsPLPTableView(state),
});

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export const productListAttributeFiltersStyles: ProductListFilterAccordionSectionStyles = {};

const ProductListAttributeFilters: FC<Props> = ({
    attributeTypeFacets,
    addProductFilters,
    removeProductFilters,
    fields,
    // XNG-Changes
    showTableLayout,
}) => {
    // TODO ISC-11787 - make showMoreLimit configurable
    const showMoreLimit = 10;

    if (!attributeTypeFacets?.length) {
        return null;
    }

    const onChangeFacet = (facet: FacetModel) => {
        if (facet.selected) {
            removeProductFilters({ attributeValueIds: [facet.id] });
        } else {
            addProductFilters({ attributeValueIds: [facet.id] });
        }
    };

    return (
        <>
            {attributeTypeFacets.map(a =>
                a.attributeValueFacets ? (
                    <ProductListFiltersAccordionSection
                        key={a.attributeTypeId}
                        title={a.nameDisplay}
                        facets={a.attributeValueFacets.map<FacetModel>(av => ({
                            ...av,
                            id: av.attributeValueId,
                            name: av.valueDisplay,
                        }))}
                        onChangeFacet={onChangeFacet}
                        showMoreLimit={showMoreLimit}
                        expandByDefault={fields.expandByDefault}
                        extendedStyles={productListCategoryFiltersStyles}
                        // XNG-Changes
                        {...{ showTableLayout }}
                    />
                ) : null,
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListAttributeFilters),
    definition: {
        group: "Product List",
        displayName: "Attribute Filters",
        // XNG-Changes: add CategoryDetailsPage
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
